import type { GameState } from "@dailywars/core/engine";
import type { RefObject } from "react";

import { DamageForecast } from "#/components/game/DamageForecast";
import { GameDockedPanelShell } from "#/components/game/GameDockedPanelShell";
import { ProductionBuildMenu } from "#/components/game/ProductionBuildMenu";
import { usePendingAttack } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

function AttackForecastOverlay({
  bottomDockRef,
}: {
  bottomDockRef: RefObject<HTMLElement | null>;
}) {
  const pendingAttack = usePendingAttack();
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const onConfirmAttack = useGameSelector((s) => s.confirmAttack);
  const onCancelAttack = useGameSelector((s) => s.cancelAttack);
  if (!pendingAttack) return null;

  return (
    <GameDockedPanelShell
      bottomDockRef={bottomDockRef}
      onCancel={onCancelAttack}
      closeAriaLabel="Cancel attack"
      panelWidth="fit"
    >
      <DamageForecast
        attackerKind={pendingAttack.attackerKind}
        attackerOwner={pendingAttack.attackerOwner}
        targetKind={pendingAttack.targetKind}
        targetOwner={pendingAttack.targetOwner}
        forecast={pendingAttack.forecast}
        counterForecast={pendingAttack.counterForecast}
        willKillTarget={pendingAttack.willKillTarget}
        willKillAttacker={pendingAttack.willKillAttacker}
        onConfirm={onConfirmAttack}
        onCancel={onCancelAttack}
        disabled={isSubmitting}
      />
    </GameDockedPanelShell>
  );
}

function BuildMenuOverlay({ bottomDockRef }: { bottomDockRef: RefObject<HTMLElement | null> }) {
  const selection = useGameSelector((s) => s.selection);
  const localState = useGameSelector((s) => s.localState);
  const cancelSelection = useGameSelector((s) => s.cancelSelection);
  const chooseBuild = useGameSelector((s) => s.chooseBuild);
  const buildingSelection = selection.mode === "building" ? selection : null;
  if (!buildingSelection) return null;

  const owner = "p1";

  return (
    <GameDockedPanelShell
      bottomDockRef={bottomDockRef}
      onCancel={cancelSelection}
      closeAriaLabel="Close build menu"
    >
      <ProductionBuildMenu
        buildingKind={buildingSelection.buildingKind}
        capacity={buildingSelection.capacity}
        funds={localState.funds[owner]}
        options={buildingSelection.options}
        owner={owner}
        onChoose={(kind) => chooseBuild(buildingSelection.pos, kind)}
        onCancel={cancelSelection}
      />
    </GameDockedPanelShell>
  );
}

export function PlayMapCanvasViewportOverlay({
  status,
  bottomDockRef,
}: {
  status: GameState["status"];
  bottomDockRef: RefObject<HTMLElement | null>;
}) {
  const pendingAttack = usePendingAttack();
  const selection = useGameSelector((s) => s.selection);
  const buildingSelection = selection.mode === "building" ? selection : null;
  if (status !== "in_progress" || !(pendingAttack || buildingSelection)) {
    return null;
  }
  return (
    <>
      {pendingAttack ? <AttackForecastOverlay bottomDockRef={bottomDockRef} /> : null}
      {buildingSelection ? <BuildMenuOverlay bottomDockRef={bottomDockRef} /> : null}
    </>
  );
}
